import { useEffect, useRef, useState } from 'preact/hooks';
import { api, ApiError } from '../api.js';
import { t } from '../i18n/index.js';
import { guestSession } from '../guestSession.js';
import { getSoundSettings, onSoundSettings, playChatSound, setSoundSettings } from '../audio/sounds.js';
import { EmojiPicker, insertAtCursor } from './EmojiPicker.js';
import { SendButton } from './SendButton.js';
import { parseMention } from '../chat/mention.js';
import { FEED, type PmState } from '../chat/usePrivateMessages.js';
import type { ArenaSocket } from '../realtime.js';
import { chatMaxLength, checkChatMessage } from '../../shared/chatRules.js';
import {
  isStaff,
  type ArenaResult,
  type ChatMessage,
  type LobbyPlayer,
  type MyRestrictions,
  type PlayerRef,
  type PrivateMessage,
  type PublicUser,
  type Role,
} from '../../shared/protocol.js';

type Tab = 'lobby' | number;

interface FeedLine {
  key: string;
  id: number;
  userId: number;
  name: string;
  role: Role;
  text: string;
  sentAt: number;
  /** Private message shown in the lobby feed (only to sender and recipient). */
  pm?: { to: PlayerRef; outgoing: boolean };
}

const time = (ms: number) => new Date(ms).toLocaleTimeString('fi-FI', { hour: '2-digit', minute: '2-digit' });
const dateTime = (ms: number) => new Date(ms).toLocaleString('fi-FI', { dateStyle: 'short', timeStyle: 'short' });

/** True while the lobby chat is on screen (it then handles private message notifications itself). */
export let lobbyChatVisible = false;

/**
 * Lobby chat and private messages (suunnitelma.md section 26). A private message is sent by
 * starting the message with "@Nickname" or by clicking a nickname (user request 2026-09-27);
 * private messages appear in the lobby feed for the two players and in their own tab.
 * Staff can delete lobby messages and close/open the lobby chat.
 */
export function LobbyChat(props: { socket: ArenaSocket; me: PublicUser; players: LobbyPlayer[]; restrictions: MyRestrictions; pm: PmState }) {
  const { socket, me, pm } = props;
  const staff = isStaff(me.role);
  const [tab, setTab] = useState<Tab>('lobby');
  const [sound, setSound] = useState(getSoundSettings());
  const [lobby, setLobby] = useState<ChatMessage[]>([]);
  const [open, setOpen] = useState(true);
  // Conversation tabs: unread ones and those used during this visit (the Messages page has all).
  const [tabIds, setTabIds] = useState<number[]>([]);
  const [lobbyUnread, setLobbyUnread] = useState(0);
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const tabRef = useRef<Tab>(tab);
  tabRef.current = tab;

  useEffect(() => onSoundSettings(setSound), []);
  useEffect(() => {
    lobbyChatVisible = true;
    pm.refreshDirectory();
    return () => {
      lobbyChatVisible = false;
      pm.view('lobbyChat', null);
    };
  }, []);
  // The lobby tab shows private messages in the feed; a conversation tab shows one conversation.
  useEffect(() => pm.view('lobbyChat', tab === 'lobby' ? FEED : tab), [tab]);

  const addTab = (id: number) => setTabIds((ids) => (ids.includes(id) ? ids : [...ids, id]));
  useEffect(() => {
    for (const c of pm.conversations) if (c.unread > 0) addTab(c.with.id);
  }, [pm.conversations]);

  useEffect(() => {
    const load = () => {
      socket.emit('lobby:history', (r: ArenaResult<{ messages: ChatMessage[]; open: boolean }>) => {
        if (r.ok) {
          setLobby(r.messages);
          setOpen(r.open);
        }
      });
    };
    const onLobby = (m: ChatMessage) => {
      setLobby((list) => [...list, m].slice(-200));
      if (m.userId !== me.id) {
        if (tabRef.current !== 'lobby') setLobbyUnread((n) => n + 1);
        playChatSound('chatMessage');
      }
    };
    const onDeleted = ({ id }: { id: number }) => setLobby((list) => list.filter((m) => m.id !== id));
    const onCleared = () => setLobby([]);
    const onState = ({ open: o }: { open: boolean }) => setOpen(o);
    const onPm = (m: PrivateMessage) => {
      addTab(m.from.id === me.id ? m.to.id : m.from.id);
      if (m.from.id !== me.id) playChatSound('chatPrivate');
    };
    load();
    socket.on('connect', load);
    socket.on('lobby:message', onLobby);
    socket.on('lobby:deleted', onDeleted);
    socket.on('lobby:cleared', onCleared);
    socket.on('lobby:state', onState);
    socket.on('pm:message', onPm);
    return () => {
      socket.off('connect', load);
      socket.off('lobby:message', onLobby);
      socket.off('lobby:deleted', onDeleted);
      socket.off('lobby:cleared', onCleared);
      socket.off('lobby:state', onState);
      socket.off('pm:message', onPm);
    };
  }, [socket, me.id]);

  const selectTab = (next: Tab) => {
    setTab(next);
    setError(null);
    if (next === 'lobby') setLobbyUnread(0);
    else pm.openConversation(next);
  };

  const conversationOf = (id: number) => pm.conversations.find((c) => c.with.id === id);
  const conversations: PlayerRef[] = tabIds.map(
    (id) => conversationOf(id)?.with ?? pm.directory.find((p) => p.id === id) ?? { id, displayName: '?' },
  );
  const online = props.players.filter((p) => p.id !== me.id);
  // "@Nickname" finds also players who are not online (decision 2026-09-29).
  const recipients: PlayerRef[] = [...pm.directory, ...online.filter((p) => !pm.directory.some((d) => d.id === p.id))];
  const activePlayer = tab === 'lobby' ? null : (conversations.find((c) => c.id === tab) ?? null);
  const partnerOnline = activePlayer ? online.some((p) => p.id === activePlayer.id) : true;
  const muted = props.restrictions.chatMute;
  const canWrite = !muted;
  const maxLength = chatMaxLength(me.role);
  const mention = tab === 'lobby' ? parseMention(text, recipients) : { kind: 'none' as const };
  const messageLength = mention.kind === 'pm' ? mention.text.length : text.trim().length;

  // Name suggestions while typing "@..." (user request 2026-09-29): every player, also those who
  // are not online (they do not show in the chat); players online first.
  const [pick, setPick] = useState(0);
  const isOnline = (id: number) => online.some((p) => p.id === id);
  const query = mention.kind === 'unknown' ? text.trimStart().slice(1).toLocaleLowerCase('fi') : null;
  const suggestions =
    query === null
      ? []
      : recipients
          .filter((p) => {
            const name = p.displayName.toLocaleLowerCase('fi');
            return name.startsWith(query) || (query.length >= 2 && name.includes(query));
          })
          .sort((a, b) => Number(isOnline(b.id)) - Number(isOnline(a.id)) || a.displayName.localeCompare(b.displayName, 'fi'))
          .slice(0, 8);
  const choose = (p: PlayerRef) => {
    setText(`@${p.displayName} `);
    setPick(0);
    setError(null);
    requestAnimationFrame(() => inputRef.current?.focus());
  };
  const onKeyDown = (e: KeyboardEvent) => {
    if (suggestions.length === 0) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setPick((i) => (i + step + suggestions.length) % suggestions.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      choose(suggestions[Math.min(pick, suggestions.length - 1)]!);
    }
  };
  /** The ✉ button: starts a private message and shows the list of players. */
  const startNewPm = () => {
    pm.refreshDirectory();
    if (tab !== 'lobby') selectTab('lobby');
    setText('@');
    setPick(0);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  /** Clicking a nickname starts a private message to that player. */
  const startPm = (name: string, userId: number) => {
    if (userId === me.id) return;
    if (tab !== 'lobby') setTab('lobby');
    setText(`@${name} `);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const send = (e: Event) => {
    e.preventDefault();
    const body = text.trim();
    if (!body) return;
    const done = (r: ArenaResult) => {
      if (r.ok) setText('');
      else setError(t.arena.errors[r.error]);
    };

    let toUserId: number | null = tab === 'lobby' ? null : tab;
    let message = body;
    if (tab === 'lobby' && mention.kind !== 'none') {
      if (mention.kind === 'unknown') return setError(t.chatUi.unknownRecipient);
      if (mention.kind === 'empty') return setError(t.chatUi.emptyPm);
      toUserId = mention.to.id;
      message = mention.text;
    }
    const rule = checkChatMessage(message, me.role);
    if (rule) return setError(t.arena.errors[rule]);
    if (toUserId === null && !open && me.role !== 'admin') return setError(t.arena.errors.chat_closed);
    setError(null);
    if (toUserId === null) socket.emit('lobby:send', { text: message }, done);
    else void pm.send(toUserId, message).then(done);
  };

  const deleteMessage = async (id: number) => {
    try {
      await api('DELETE', `/api/mod/lobby/messages/${id}`);
    } catch (err) {
      setError(t.errors[err instanceof ApiError ? err.code : 'network']);
    }
  };

  const closeTab = (id: number) => {
    setTabIds((ids) => ids.filter((c) => c !== id));
    if (tab === id) setTab('lobby');
  };

  const pmLine = (m: PrivateMessage, pmInFeed: boolean): FeedLine => ({
    key: `p${m.id}`,
    id: m.id,
    userId: m.from.id,
    name: m.from.displayName,
    role: m.fromRole,
    text: m.text,
    sentAt: m.sentAt,
    pm: pmInFeed ? { to: m.to, outgoing: m.from.id === me.id } : undefined,
  });

  const lines: FeedLine[] =
    tab === 'lobby'
      ? [
          ...lobby.map((m) => ({
            key: `l${m.id}`,
            id: m.id,
            userId: m.userId,
            name: m.displayName,
            role: m.role ?? 'player',
            text: m.text,
            sentAt: m.sentAt,
          })),
          ...pm.feed.map((m) => pmLine(m, true)),
        ].sort((a, b) => a.sentAt - b.sentAt)
      : (pm.messages.get(tab) ?? []).map((m) => pmLine(m, false));

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [tab, lines.length]);

  const nick = (l: { userId: number; name: string; role: Role }, label = l.name) => (
    <button
      type="button"
      class={`chat-nick role-${l.role} ${l.userId === me.id ? 'self' : ''}`}
      onClick={() => startPm(l.name, l.userId)}
      title={l.userId === me.id ? undefined : t.chatUi.clickToPm(l.name)}
      disabled={l.userId === me.id}
    >
      {label}
    </button>
  );

  return (
    <section class="card lobby-chat">
      <div class="section-head">
        <h2>{t.lobbyChat.title}</h2>
        <div class="chat-head-actions">
          {!guestSession.guest && (
            <button class="btn btn-ghost btn-small" onClick={startNewPm} title={t.chatUi.newPmTitle}>
              ✉ {t.chatUi.newPm}
            </button>
          )}
          <a class="btn btn-ghost btn-small" href="#/viestit">
            {t.lobbyChat.allMessages}
          </a>
          <button
            class={`btn btn-ghost btn-icon ${sound.chatSounds ? '' : 'off'}`}
            onClick={() => setSoundSettings({ ...sound, chatSounds: !sound.chatSounds })}
            title={sound.chatSounds ? t.sound.chatMute : t.sound.chatUnmute}
            aria-label={sound.chatSounds ? t.sound.chatMute : t.sound.chatUnmute}
            aria-pressed={sound.chatSounds}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
              <path d="M6 16V11a6 6 0 0 1 12 0v5l2 2H4z" />
              <path d="M10 20a2 2 0 0 0 4 0" />
              {!sound.chatSounds && <path d="M3 3l18 18" />}
            </svg>
          </button>
        </div>
      </div>

      {conversations.length > 0 && (
        <div class="chat-tabs" role="tablist">
          <button role="tab" aria-selected={tab === 'lobby'} class={`chat-tab ${tab === 'lobby' ? 'active' : ''}`} onClick={() => selectTab('lobby')}>
            {t.lobbyChat.lobbyTab}
            {lobbyUnread > 0 && <span class="tab-unread">{lobbyUnread}</span>}
          </button>
          {conversations.map((c) => (
            <span class={`chat-tab ${tab === c.id ? 'active' : ''}`}>
              <button role="tab" aria-selected={tab === c.id} class="chat-tab-name" onClick={() => selectTab(c.id)}>
                ✉ {c.displayName}
                {(conversationOf(c.id)?.unread ?? 0) > 0 && tab !== c.id && <span class="tab-unread">{conversationOf(c.id)!.unread}</span>}
              </button>
              <button class="chat-tab-close" onClick={() => closeTab(c.id)} aria-label={t.lobbyChat.closeTab} title={t.lobbyChat.closeTab}>
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      {tab === 'lobby' && !open && <p class="notice">{t.lobbyChat.closed}</p>}
      {tab !== 'lobby' && !partnerOnline && activePlayer && <p class="notice">{t.lobbyChat.offline(activePlayer.displayName)}</p>}
      {muted && <p class="error">{t.restrictions.chatMuted(muted.until ? dateTime(muted.until) : null)}</p>}

      <div class="lobby-messages" ref={listRef}>
        {lines.length === 0 && <p class="muted chat-empty">{tab === 'lobby' ? t.lobbyChat.empty : t.lobbyChat.pmEmpty}</p>}
        {/* One line per message, all left-aligned: time, nickname (staff coloured, click = private message) and text. */}
        {lines.map((l) => (
          <div class={`chat-line ${l.userId === me.id ? 'mine' : ''} ${l.pm ? 'pm' : ''}`} key={l.key}>
            <span class="chat-time">{time(l.sentAt)}</span>{' '}
            {l.pm && (
              <span class="pm-tag" title={t.chatUi.privateTag} aria-label={t.chatUi.privateTag} role="img">
                🔒
              </span>
            )}{' '}
            {l.pm?.outgoing ? (
              <>
                {t.lobbyChat.you} → {nick({ userId: l.pm.to.id, name: l.pm.to.displayName, role: 'player' })}
              </>
            ) : l.pm ? (
              <>
                {nick(l)} → {t.chatUi.toYou}
              </>
            ) : (
              nick(l)
            )}
            : <span class="chat-text">{l.text}</span>
            {staff && tab === 'lobby' && !l.pm && (
              <button class="msg-delete" onClick={() => void deleteMessage(l.id)} title={t.lobbyChat.delete} aria-label={t.lobbyChat.delete}>
                🗑
              </button>
            )}
          </div>
        ))}
      </div>

      {error && <p class="error">{error}</p>}
      {guestSession.guest ? (
        <p class="chat-readonly">{t.guest.readOnly}</p>
      ) : (
        <form class="chat-form" onSubmit={send}>
          {suggestions.length > 0 && (
            <ul class="mention-suggest" role="listbox" aria-label={t.chatUi.suggestions}>
              {suggestions.map((p, i) => (
                <li key={p.id} role="option" aria-selected={i === pick}>
                  <button type="button" class={i === pick ? 'active' : ''} onMouseDown={(e) => e.preventDefault()} onClick={() => choose(p)}>
                    <span class={`status-dot ${isOnline(p.id) ? 'status-available' : 'status-offline'}`} />
                    <span class="suggest-name">{p.displayName}</span>
                    {!isOnline(p.id) && <span class="suggest-note">{t.chatUi.notOnline}</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div class="chat-input-row">
            <input
              ref={inputRef}
              value={text}
              onInput={(e) => {
                setText(e.currentTarget.value);
                setPick(0);
                setError(null);
              }}
              onKeyDown={onKeyDown}
              placeholder={tab === 'lobby' ? t.lobbyChat.placeholder : t.lobbyChat.pmPlaceholder(activePlayer?.displayName ?? '')}
              maxLength={maxLength + (tab === 'lobby' ? 40 : 0)}
              disabled={!canWrite}
              aria-label={t.lobbyChat.placeholder}
            />
            <SendButton
              label={mention.kind === 'pm' ? t.chatUi.sendPm : t.lobbyChat.send}
              private={mention.kind === 'pm'}
              disabled={!canWrite || !text.trim()}
            />
          </div>
          <div class="chat-tools">
            <EmojiPicker disabled={!canWrite} onPick={(emoji) => setText(insertAtCursor(inputRef.current, text, emoji))} />
            <span class={`char-count ${messageLength > maxLength - 20 ? 'near' : ''}`} aria-hidden="true">
              {messageLength}/{maxLength}
            </span>
          </div>
        </form>
      )}
      <p class="hint privacy-note">
        {t.chatUi.pmHint} {t.lobbyChat.privacy}
      </p>
    </section>
  );
}
